import { supabase } from '../lib/supabase';
import errorTrackingService from './errorTrackingService';

export const authService = {
  /**
   * Sign up a new user with email, password, and agent credentials
   * Returns user data even if email confirmation is required
   */
  async signUp({ email, password, fullName, phoneNumber, companyName, state }) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            phone_number: phoneNumber,
            company_name: companyName,
            state: state
          },
          emailRedirectTo: `${window?.location?.origin}/user-login`
        }
      });

      if (error) throw error;

      // Check if email confirmation is required
      const emailConfirmationRequired = data?.user && !data?.session;

      return { 
        data, 
        error: null,
        emailConfirmationRequired 
      };
    } catch (error) {
      console.error('Sign up error:', error);
      errorTrackingService?.captureApiError(
        '/auth/signup',
        'POST',
        error,
        { email, fullName, companyName, state },
        null
      );
      return { data: null, error: error?.message || 'Failed to create account', emailConfirmationRequired: false };
    }
  },

  /**
   * Sign in existing user
   */
  async signIn({ email, password }) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      errorTrackingService?.captureApiError(
        '/auth/signin',
        'POST',
        error,
        { email },
        null
      );
      return { data: null, error: error?.message || 'Failed to sign in' };
    }
  },

  /**
   * Sign out current user with retry logic
   */
  async signOut() {
    try {
      console.log('[Auth Service] Attempting to sign out...');
      
      const { error } = await supabase?.auth?.signOut();
      
      if (error) {
        console.error('[Auth Service] Sign out error:', error);
        
        // Check if it's a network error that might be transient
        if (error?.message?.includes('fetch') || error?.message?.includes('network') || error?.status === 0) {
          console.log('[Auth Service] Network error detected, retrying once...');
          
          // Wait 1 second and retry
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const { error: retryError } = await supabase?.auth?.signOut();
          
          if (retryError) {
            console.error('[Auth Service] Retry sign out failed:', retryError);
            throw retryError;
          }
          
          console.log('[Auth Service] Sign out succeeded on retry');
          return { error: null };
        }
        
        throw error;
      }

      console.log('[Auth Service] Sign out successful');
      return { error: null };
    } catch (error) {
      console.error('[Auth Service] Sign out error:', error);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to sign out';
      
      if (error?.message?.includes('fetch') || error?.message?.includes('network')) {
        errorMessage = 'Network error during sign out. Please check your connection and try again.';
      } else if (error?.message?.includes('CORS')) {
        errorMessage = 'Connection error during sign out. This may be a temporary issue. Please try again.';
      }
      
      errorTrackingService?.captureApiError(
        '/auth/signout',
        'POST',
        error,
        null,
        null
      );
      
      return { error: errorMessage };
    }
  },

  /**
   * Get current session
   */
  async getSession() {
    try {
      const { data, error } = await supabase?.auth?.getSession();
      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Get session error:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Get current user profile
   */
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Get user profile error:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Update user profile
   */
  async updateUserProfile(userId, updates) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', userId)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Update user profile error:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Send password reset email
   */
  async sendPasswordResetEmail(email) {
    try {
      // Use current origin for redirect URL to support all environments
      const currentOrigin = window?.location?.origin || 'https://offersense.ai';
      const redirectUrl = `${currentOrigin}/password-reset`;
      
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: redirectUrl
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Send password reset error:', error);
      return { data: null, error: error?.message || 'Failed to send reset email' };
    }
  },

  /**
   * Update password with reset token
   */
  async updatePassword(newPassword) {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Update password error:', error);
      return { data: null, error: error?.message || 'Failed to update password' };
    }
  }
};